/**
 * Utility to handle async listener errors that commonly occur with Chrome extensions
 * and React applications
 */

// Track if we've already added the global error handler
let globalHandlerAdded = false;

/**
 * Initialize global error handling for async listener errors
 */
export const initializeAsyncErrorHandler = () => {
  if (globalHandlerAdded || typeof window === 'undefined') {
    return;
  }

  // Handle unhandled promise rejections that might cause async listener errors
  window.addEventListener('unhandledrejection', (event) => {
    // Check if it's the specific async listener error
    if (event.reason && 
        typeof event.reason.message === 'string' && 
        event.reason.message.includes('message channel closed before a response was received')) {
      
      console.warn('Caught async listener error (likely from browser extension):', event.reason.message);
      
      // Prevent the error from propagating and showing in console
      event.preventDefault();
      
      // You can optionally report this to your error tracking service
      // errorTrackingService.reportWarning('async-listener-error', event.reason);
    }
  });

  // Handle general errors that might be related
  window.addEventListener('error', (event) => {
    if (event.error && 
        typeof event.error.message === 'string' && 
        event.error.message.includes('message channel closed before a response was received')) {
      
      console.warn('Caught async listener error (likely from browser extension):', event.error.message);
      
      // Prevent the error from propagating
      event.preventDefault();
    }
  });

  globalHandlerAdded = true;
};

/**
 * Wrapper for async functions to catch and handle listener errors
 */
export const safeAsync = (asyncFn) => {
  return async (...args) => {
    try {
      return await asyncFn(...args);
    } catch (error) {
      if (error.message && error.message.includes('message channel closed before a response was received')) {
        console.warn('Safely handled async listener error:', error.message);
        return null; // or appropriate fallback value
      }
      throw error; // Re-throw other errors
    }
  };
};

/**
 * Safe event listener wrapper that handles cleanup properly
 */
export const safeEventListener = (element, eventType, handler, options = {}) => {
  const safeHandler = (event) => {
    try {
      handler(event);
    } catch (error) {
      if (error.message && error.message.includes('message channel closed before a response was received')) {
        console.warn('Safely handled async listener error in event handler:', error.message);
      } else {
        console.error('Error in event handler:', error);
      }
    }
  };

  element.addEventListener(eventType, safeHandler, options);

  // Return cleanup function
  return () => {
    try {
      element.removeEventListener(eventType, safeHandler, options);
    } catch (error) {
      console.warn('Error removing event listener:', error);
    }
  };
};

/**
 * Cleanup function to remove global error handlers (call on app unmount)
 */
export const cleanupAsyncErrorHandler = () => {
  globalHandlerAdded = false;
  // Note: We don't actually remove the handlers as they're needed throughout the app lifecycle
};